import React, { useCallback, useRef } from 'react';
import DataCardLoader from '../Pages/Dashboard/components/SkeletonLoader/DataCardLoader';
import ImgCardLoader from '../Pages/Dashboard/components/SkeletonLoader/ImgCardLoader';

/**
 * Manages an infinite scroll feature for loading more data upon reaching the bottom of a component.
 * @example
 * InfiniteScroll({ fetchMoreData: myFetchFunction, hasMore: true, children: myContent, loading: false, className: 'my-class', isLocation: true });
 * Returns a JSX element containing the specified children and loader.
 * @param {function} fetchMoreData - Function to fetch more data when the scroll reaches the bottom.
 * @param {boolean} hasMore - Indicates if there is more data to fetch.
 * @param {React.ReactNode} children - Elements to be displayed within the scrollable container.
 * @param {boolean} loading - Indicates if data is currently being loaded.
 * @param {string} className - Additional class names for styling the scrollable container.
 * @param {boolean} isLocation - Determines the type of loader to display when loading.
 * @returns {JSX.Element} A scrollable div element that loads more data as the user scrolls to the bottom.
 * @description
 *   - Utilizes a `useRef` to track the scrollable element.
 *   - Uses `useCallback` for the scroll event handler to improve performance by memoizing the function.
 *   - Adds and removes scroll event listeners via `useEffect` to manage component lifecycle.
 *   - Dynamically includes different loader components based on the `loading` and `isLocation` properties.
 */
const InfiniteScrollModal = ({
  fetchMoreData,
  hasMore,
  children,
  loading,
  className,
  isLocation,
}: any) => {
  const modalRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    // If already loading or there is no more data to load, do nothing
    if (loading || !hasMore) return;

    const modalElement = modalRef.current;
    if (!modalElement) return;

    const { scrollTop, scrollHeight, clientHeight } = modalElement;

    // Check if the user has scrolled to the bottom
    if (scrollTop + clientHeight >= scrollHeight - 10) {
      // Call the function to fetch more data
      fetchMoreData();
    }
  }, [loading, hasMore, fetchMoreData]);

  React.useEffect(() => {
    const modalElement = modalRef.current;

    if (modalElement) {
      // Add scroll event listener
      modalElement.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (modalElement) {
        // Clean up the scroll event listener on unmount
        modalElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, [handleScroll]);

  return (
    <div
      ref={modalRef}
      className={`infiniteScroll ${loading ? 'loadingTrue' : ''} ${
        className && className
      }`}
    >
      {<>{children}</>}
      {loading && isLocation ? (
        <div className="loadingUser">
          <DataCardLoader className="elevate" />
          {/* <Loading type="div" height="auto" /> */}
        </div>
      ) : (
        loading && <ImgCardLoader className="img-card-loader-sm" />
      )}
    </div>
  );
};

export default InfiniteScrollModal;
